// 301 redirects from legacy URLs that still hold Google rankings to their
// current canonical pages. Keys are lowercased, trailing-slash-stripped,
// URL-decoded pathnames.
export const LEGACY_REDIRECTS: Record<string, string> = {
  "/ag-tarps/stocktrailer": "/stock-trailer-covers",
  "/sail-shade,-tension-struc": "/sail-shades",
  "/sail-shade,-tension-structures": "/sail-shades",
  "/indoor-&-outdoor-curtains": "/curtains",
  "/tarps-&-covers": "/covers-tops",
  "/covers-&-tops": "/covers-tops",
  "/projects-for-marketing": "/projects",
  "/patio-canopy": "/pergola-covers",
  "/patio-covers": "/pergola-covers",
  "/pool-&-spa": "/pool-spa",
  "/boat-covers-&-tops": "/boat-covers",
  "/solair": "/retractable-awnings",
  "/sun-wind-curtains": "/curtains",
  "/series/alamo": "/alamo-series",
  "/series/bluebonnet": "/bluebonnet-series",
  "/series/hill-country": "/hill-country-series",
  "/retractable/smart-controls": "/lone-star-elite",
  "/retractable/downloads": "/retractable-awnings",
  "/retractable/why-solair": "/retractable-awnings",
  "/retractable/installation": "/retractable-awnings",
  "/financing": "/residential-awnings#financing",
  "/retractable/fabric-collections": "/retractable-awnings",
  "/retractable/compare-series": "/retractable-awnings",
  "/retractable-systems": "/retractable-awnings",
  "/cushions": "/covers-tops",

  // --- Consolidated duplicate-intent awning + city pages ---
  "/texas-awnings": "/awnings",
  "/awnings-by-city": "/service-areas",
  "/awning-company": "/awnings",
  "/awning-company/in/austin": "/austin",
  "/awning-company/in/san-antonio": "/san-antonio",

  // --- Louvered pergola category: brand-named & wording variants → canonical hub ---
  "/pergolux": "/louvered-pergolas",
  "/pergolux-pergolas": "/louvered-pergolas",
  "/pergolux-louvered-pergolas": "/louvered-pergolas",
  "/pergolux-dealer": "/louvered-pergolas",
  "/pergolux-dealer-texas": "/louvered-pergolas",
  "/louvered-pergolas-pergolux": "/louvered-pergolas",
  "/louvered-pergola": "/louvered-pergolas",
  "/louvered-roof": "/louvered-pergolas",
  "/louvered-roofs": "/louvered-pergolas",
  "/louvered-roof-systems": "/louvered-pergolas",
  "/louvered-patio-covers": "/louvered-pergolas",
  "/motorized-pergola": "/louvered-pergolas",
  "/motorized-pergolas": "/louvered-pergolas",
  "/motorized-louvered-pergola": "/louvered-pergolas",
  "/motorized-louvered-pergolas": "/louvered-pergolas",
  "/aluminum-pergola": "/louvered-pergolas",
  "/aluminum-pergolas": "/louvered-pergolas",
  "/opening-roof-pergolas": "/louvered-pergolas",
  "/adjustable-louver-pergolas": "/louvered-pergolas",
  "/outdoor-living/louvered-pergolas": "/louvered-pergolas",
  "/products/louvered-pergolas": "/louvered-pergolas",
  "/services/louvered-pergolas": "/louvered-pergolas",
};

/** Cities that have a dedicated /louvered-patio-covers/in/[city] page. */
const LOUVERED_CITY_SLUGS = new Set(["san-antonio", "austin"]);

/** Legacy path prefixes that used to serve louvered pergola city pages. */
const LOUVERED_CITY_PREFIXES = [
  "/louvered-pergolas/in",
  "/louvered-pergola/in",
  "/louvered-patio-cover/in",
  "/motorized-pergolas/in",
  "/motorized-pergola/in",
  "/motorized-louvered-pergolas/in",
  "/aluminum-pergolas/in",
  "/pergolux/in",
  "/pergolux-pergolas/in",
  "/pergolux-louvered-pergolas/in",
  "/louvered-pergolas-pergolux/in",
];

/** Legacy prefixes for the fabric patio & pergola cover city pages. */
const PERGOLA_COVER_CITY_PREFIXES = [
  "/patio-covers/in",
  "/patio-canopy/in",
  "/pergola-cover/in",
  "/pergolas/in",
  "/pergola/in",
];

const CITY_SLUG_RE = /^[a-z0-9-]+$/;

/**
 * Pattern-based redirects for city URL variants. Anything under a legacy
 * louvered prefix goes to the dedicated louvered city page when one exists,
 * otherwise to the brand-agnostic louvered pergola hub. Legacy fabric
 * patio-cover prefixes map to /pergola-covers/in/[city].
 */
function resolvePatternRedirect(p: string): string | null {
  const lower = p.toLowerCase();

  // Normalize the mixed-capitalization legacy URL reported by Search Console.
  if (lower === "/backlit-awnings" && p !== "/backlit-awnings") {
    return "/backlit-awnings";
  }

  // Retired city x "awning company" combination pages: send the remaining
  // variants to the Service Areas hub instead of a near-duplicate city page.
  if (lower.startsWith("/awning-company/in/") || lower.startsWith("/awnings-by-city/")) {
    return "/service-areas";
  }

  // Old brand-suffixed city URLs, e.g. /louvered-patio-covers/in/austin-pergolux
  const brandStripped = lower.replace(/-(pergolux|struxure|azenco|brustor|equinox)$/, "");

  for (const prefix of LOUVERED_CITY_PREFIXES) {
    if (brandStripped.startsWith(`${prefix}/`)) {
      const city = brandStripped.slice(prefix.length + 1);
      if (!CITY_SLUG_RE.test(city)) return "/louvered-pergolas";
      return LOUVERED_CITY_SLUGS.has(city)
        ? `/louvered-patio-covers/in/${city}`
        : "/louvered-pergolas";
    }
  }

  // Canonical louvered city prefix, but a city without its own page.
  if (brandStripped.startsWith("/louvered-patio-covers/in/")) {
    const city = brandStripped.slice("/louvered-patio-covers/in/".length);
    if (!LOUVERED_CITY_SLUGS.has(city)) return "/louvered-pergolas";
    if (brandStripped !== lower || p !== brandStripped) {
      return `/louvered-patio-covers/in/${city}`;
    }
    return null;
  }

  for (const prefix of PERGOLA_COVER_CITY_PREFIXES) {
    if (brandStripped.startsWith(`${prefix}/`)) {
      const city = brandStripped.slice(prefix.length + 1);
      if (!CITY_SLUG_RE.test(city)) return "/pergola-covers";
      return `/pergola-covers/in/${city}`;
    }
  }

  // Brand-suffixed variant of the canonical fabric city route.
  if (brandStripped.startsWith("/pergola-covers/in/") && brandStripped !== lower) {
    return brandStripped;
  }

  return null;
}

export function resolveLegacyRedirect(pathname: string): string | null {
  let p: string;
  try {
    p = decodeURIComponent(pathname);
  } catch {
    p = pathname;
  }
  p = p.replace(/\/+$/, "") || "/";
  return (
    LEGACY_REDIRECTS[p] ??
    LEGACY_REDIRECTS[p.toLowerCase()] ??
    resolvePatternRedirect(p)
  );
}
